import { Controller } from "@hotwired/stimulus"
import { nativeHaptic } from "../utils/native_bridge"

// iOS-style touch swipes for task outline rows (touch pointers only; desktop
// mouse is untouched). Right-swipe past the threshold toggles complete.
// Left-swipe partially reveals a red Delete button (tap to confirm, tap
// anywhere else or scroll to close); a full left-swipe commits the delete
// directly. Rows with subtasks don't offer swipe-delete (same "delete
// subtasks first" rule as the row menu). The actual mutations live in
// tasks-outline, wired through task-swipe:toggle / task-swipe:delete events
// on the shared element, so this controller stays purely gestural.
//
// The colored action layer is a fixed-position element sized to the revealed
// gap: while a swipe is claimed the page can't scroll (preventDefault +
// touch-action: pan-y), and the open reveal closes on any scroll, so fixed
// coordinates stay valid for the layer's whole lifetime.
export default class extends Controller {
    CLAIM_PX = 24
    REVEAL_PX = 88
    COMPLETE_RATIO = 0.35
    DELETE_RATIO = 0.55

    initialize() {
        this._onPointerDown = this._pointerDown.bind(this)
        this._onPointerMove = this._pointerMove.bind(this)
        this._onPointerUp = this._pointerUp.bind(this)
        this._onPointerCancel = this._pointerCancel.bind(this)
        this._onScrollAnywhere = this._closeReveal.bind(this)
    }

    connect() {
        this._swipe = null
        this._open = null
        this.element.addEventListener("pointerdown", this._onPointerDown)
        this.element.addEventListener("pointermove", this._onPointerMove)
        this.element.addEventListener("pointerup", this._onPointerUp)
        this.element.addEventListener("pointercancel", this._onPointerCancel)
    }

    disconnect() {
        this.element.removeEventListener("pointerdown", this._onPointerDown)
        this.element.removeEventListener("pointermove", this._onPointerMove)
        this.element.removeEventListener("pointerup", this._onPointerUp)
        this.element.removeEventListener("pointercancel", this._onPointerCancel)
        if (this._swipe) this._cleanupSwipe(this._swipe)
        this._closeReveal(true)
    }

    _pointerDown(e) {
        if (e.pointerType !== "touch" || !e.isPrimary) return
        if (this._open) {
            // Tap on the revealed Delete button commits (its click handler);
            // a touch anywhere else just closes the reveal.
            if (!e.target.closest(".task-swipe-layer")) this._closeReveal()
            return
        }
        if (this._swipe) return
        let row = e.target.closest("[data-task-row]")
        if (!row || !this.element.contains(row)) return
        if (row.classList.contains("is-editing") || row.dataset.taskSwipeBusy) return
        // Unsaved empty-list placeholder: no record to complete or delete yet.
        if (row.dataset.placeholderPending) return
        // The drag handle owns its own gesture; menus/actions are plain taps.
        if (e.target.closest(".tasks-outline-handle, .tasks-outline-actions, .dropdown-menu")) return
        this._swipe = { row: row, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, width: row.offsetWidth, rect: null, layer: null, claimed: false, dir: 0, dx: 0, past: false }
    }

    _pointerMove(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        let dx = e.clientX - s.startX
        let dy = e.clientY - s.startY

        if (!s.claimed) {
            // Axis lock: clearly-vertical drags go back to the scroller.
            if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
                this._swipe = null
                return
            }
            if (Math.abs(dx) < this.CLAIM_PX || Math.abs(dx) < Math.abs(dy) * 1.2) return
            s.dir = dx > 0 ? 1 : -1
            // A section heading is never completed, so it has no right swipe.
            if (s.dir > 0 && s.row.dataset.textStyle) {
                this._swipe = null
                return
            }
            if (s.dir < 0 && this._rowHasChildren(s.row)) {
                this._swipe = null
                return
            }
            s.claimed = true
            s.rect = s.row.getBoundingClientRect()
            s.layer = this._buildLayer(s)
            s.row.classList.add("is-task-swiping")
            try { s.row.setPointerCapture(e.pointerId) } catch { /* detached row */ }
        }

        e.preventDefault()
        s.dx = s.dir > 0 ? Math.max(dx, 0) : Math.min(dx, 0)
        this._render(s)
        let ratio = s.dir > 0 ? this.COMPLETE_RATIO : this.DELETE_RATIO
        let past = Math.abs(s.dx) >= s.width * ratio
        if (past !== s.past) {
            s.past = past
            s.layer.classList.toggle("is-past-threshold", past)
            if (past) nativeHaptic("light")
        }
    }

    _pointerUp(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        this._swipe = null
        if (!s.claimed) return
        this._suppressNextClick()

        if (s.dir > 0) {
            if (Math.abs(s.dx) >= s.width * this.COMPLETE_RATIO) {
                nativeHaptic("medium")
                this.dispatch("toggle", { detail: { row: s.row } })
            }
            this._springBack(s)
        } else if (Math.abs(s.dx) >= s.width * this.DELETE_RATIO) {
            this._commitDelete(s)
        } else if (Math.abs(s.dx) >= this.REVEAL_PX * 0.5) {
            this._openReveal(s)
        } else {
            this._springBack(s)
        }
    }

    _pointerCancel(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        this._swipe = null
        if (s.claimed) this._springBack(s)
    }

    // ----- Rendering -----

    _buildLayer(s) {
        let kind = "delete"
        if (s.dir > 0) kind = s.row.dataset.completed === "true" ? "uncomplete" : "complete"
        let icons = { complete: "bi-check-lg", uncomplete: "bi-arrow-counterclockwise", delete: "bi-trash" }
        let labels = { complete: "Done", uncomplete: "Undo", delete: "Delete" }
        let layer = document.createElement("div")
        layer.className = `task-swipe-layer task-swipe-layer--${kind}`
        layer.setAttribute("data-turbo-temporary", "true")
        layer.style.top = `${s.rect.top}px`
        layer.style.height = `${s.rect.height}px`
        layer.style.width = "0px"
        layer.innerHTML = `<i class="bi ${icons[kind]}"></i><span>${labels[kind]}</span>`
        document.body.appendChild(layer)
        return layer
    }

    _render(s) {
        s.row.style.transform = `translateX(${s.dx}px)`
        let w = Math.abs(s.dx)
        s.layer.style.width = `${w}px`
        s.layer.style.left = s.dir > 0 ? `${s.rect.left}px` : `${s.rect.right - w}px`
    }

    _springBack(s) {
        this._settle(s)
        s.row.style.transform = ""
        s.layer.style.width = "0px"
        if (s.dir < 0) s.layer.style.left = `${s.rect.right}px`
        setTimeout(() => this._cleanupSwipe(s), 220)
    }

    _settle(s) {
        s.row.classList.add("is-task-swipe-settling")
        s.layer.classList.add("is-settling")
    }

    _cleanupSwipe(s) {
        if (s.layer) s.layer.remove()
        s.row.classList.remove("is-task-swiping", "is-task-swipe-settling")
        if (!s.row.dataset.taskSwipeBusy) s.row.style.transform = ""
    }

    // ----- Delete reveal / commit -----

    _openReveal(s) {
        this._settle(s)
        s.row.style.transform = `translateX(-${this.REVEAL_PX}px)`
        s.layer.style.width = `${this.REVEAL_PX}px`
        s.layer.style.left = `${s.rect.right - this.REVEAL_PX}px`
        s.layer.classList.add("is-open")
        s.layer.classList.remove("is-past-threshold")
        s.layer.addEventListener("click", () => this._commitDelete(this._open), { once: true })
        this._open = s
        document.addEventListener("scroll", this._onScrollAnywhere, { capture: true, passive: true })
    }

    _closeReveal(immediate = false) {
        let s = this._open
        if (!s) return
        this._open = null
        document.removeEventListener("scroll", this._onScrollAnywhere, { capture: true })
        if (immediate) {
            this._cleanupSwipe(s)
            return
        }
        s.row.style.transform = ""
        s.layer.style.width = "0px"
        s.layer.style.left = `${s.rect.right}px`
        setTimeout(() => this._cleanupSwipe(s), 220)
    }

    _commitDelete(s) {
        if (!s) return
        if (this._open === s) {
            this._open = null
            document.removeEventListener("scroll", this._onScrollAnywhere, { capture: true })
        }
        nativeHaptic("medium")
        s.row.dataset.taskSwipeBusy = "1"
        this._settle(s)
        s.row.style.transform = `translateX(-${s.width + 40}px)`
        s.layer.style.width = `${s.rect.width}px`
        s.layer.style.left = `${s.rect.left}px`
        setTimeout(() => {
            // Clear gesture styling first so the block is pristine if the
            // server refuses the delete and tasks-outline restores it.
            delete s.row.dataset.taskSwipeBusy
            this._cleanupSwipe(s)
            this.dispatch("delete", { detail: { row: s.row } })
        }, 180)
    }

    // ----- Helpers -----

    _rowHasChildren(row) {
        let container = this.element.querySelector(`[data-children-for="${CSS.escape(row.dataset.taskId)}"]`)
        return !!container && !!container.querySelector("[data-task-block]")
    }

    // A completed swipe must not double as the row's tap (startEdit).
    _suppressNextClick() {
        let swallow = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }
        this.element.addEventListener("click", swallow, { capture: true, once: true })
        setTimeout(() => this.element.removeEventListener("click", swallow, { capture: true }), 400)
    }
}
